import React, { useCallback, useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  ActionButton,
  AddUserButton,
  Avatar,
  ContactItem,
  ContactList,
  Content,
  Header,
  HeaderTitle,
  Page,
  PageShell,
  RoleBadge,
  UserCard,
  UserDetails,
  UserHeading,
  UserIdentity,
  UserList,
  UserListState,
  UserName,
  UsersToolbar,
  UserCount,
} from "./styles.manageUsers";
import {
  getCachedOutletUsers,
  getOutletUsers,
  getOutletUsersErrorMessage,
  maskUserPhone,
  setCachedOutletUsers,
} from "./manageUsersApi";
import { getSelectedOutletId } from "./outletStorage";

const ManageUsers = ({ isBackdrop = false }) => {
  const navigate = useNavigate();
  const outletId = getSelectedOutletId();
  const [users, setUsers] = useState(() => getCachedOutletUsers(outletId) || []);
  const [isLoading, setIsLoading] = useState(
    () => !isBackdrop && !getCachedOutletUsers(outletId)
  );
  const [errorMessage, setErrorMessage] = useState("");

  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage("");

    try {
      const loadedUsers = await getOutletUsers(outletId);
      setCachedOutletUsers(outletId, loadedUsers);
      setUsers(loadedUsers);
    } catch (error) {
      setUsers([]);
      setErrorMessage(getOutletUsersErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  }, [outletId]);

  useEffect(() => {
    // Backdrop is inert decoration and never fetches. The list serves the cache
    // when present and only fetches on first load or after an add invalidates it.
    if (isBackdrop) return;
    const cached = getCachedOutletUsers(outletId);
    if (cached) {
      setUsers(cached);
      setIsLoading(false);
      return;
    }
    loadUsers();
  }, [isBackdrop, outletId, loadUsers]);

  const userCountLabel =
    isLoading || errorMessage
      ? "Users"
      : `${users.length} ${users.length === 1 ? "user" : "users"}`;

  return (
    <Page>
      <PageShell>
        <Header>
          <ActionButton
            type="button"
            aria-label="Go back"
            onClick={() => navigate("/")}
          >
            <img src="/logos/back_logo.svg" alt="" />
          </ActionButton>
          <HeaderTitle>Manage users</HeaderTitle>
        </Header>

        <Content>
          <UsersToolbar>
            <UserCount>{userCountLabel}</UserCount>
            <AddUserButton
              type="button"
              onClick={() => navigate("/manage-users/add")}
            >
              <span>Add new</span>
              <Plus aria-hidden="true" />
            </AddUserButton>
          </UsersToolbar>

          <UserList>
            {isLoading && (
              <UserListState role="status" aria-live="polite">
                <span className="spinner" aria-hidden="true" />
                <strong>Loading users</strong>
                <p>Fetching users for this outlet…</p>
              </UserListState>
            )}

            {!isLoading && errorMessage && (
              <UserListState role="alert">
                <strong>Unable to load users</strong>
                <p>{errorMessage}</p>
                <button type="button" onClick={loadUsers}>
                  Try again
                </button>
              </UserListState>
            )}

            {!isLoading && !errorMessage && users.length === 0 && !isBackdrop && (
              <UserListState aria-live="polite">
                <strong>No users found</strong>
                <p>No users are currently mapped to this outlet.</p>
              </UserListState>
            )}

            {!isLoading &&
              !errorMessage &&
              users.map((user) => (
                <UserCard key={user.id}>
                  <UserIdentity>
                    <Avatar aria-hidden="true">{user.initials}</Avatar>
                    <UserDetails>
                      <UserHeading>
                        <UserName>{user.name}</UserName>
                        {user.role && (
                          <RoleBadge $tone={user.roleTone}>
                            {user.role}
                          </RoleBadge>
                        )}
                      </UserHeading>
                      <ContactList>
                        {user.phone && (
                          <ContactItem>{maskUserPhone(user.phone)}</ContactItem>
                        )}
                        {user.email && (
                          <ContactItem>{user.email}</ContactItem>
                        )}
                      </ContactList>
                    </UserDetails>
                  </UserIdentity>

                </UserCard>
              ))}
          </UserList>
        </Content>
      </PageShell>
    </Page>
  );
};

export default ManageUsers;
